import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { Tooltip } from '@/components/AdForm/components/Tooltip';
import * as S from '@/components/AdForm/AdForm.styles';
import { Button } from '@/components/UI/Button/Button';
import { Input } from '@/components/UI/Input/Input';
import {
  EditAdFormProps,
  EditAdError,
  EditAdFormFields,
} from '@/components/AdForm/AdForm.types';
import { Textarea } from '@/components/UI/Textarea/Textarea';
import useScreenSize from '@/hooks/useScreenSize';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { ROUTES } from '@/constants/routes';
import { ContactTypeFormSelect } from '@/components/AdForm/components/ContactTypeFormSelect';
import { AdImages } from '@/components/AdImages/AdImages';
import { useServerDataContext } from '@/contexts';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { RequiredStar } from '@/components/UI/RequiredStar/RequiredStar';
import { PropertyType } from '@/api/types/categories.types';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import { UpdateAdResponse } from '@/api/types/ads.types';
import { UPDATE_AD } from '@/api/mutations/ads.mutations';
import {
  buildUpdateAdAttributes,
  mapApiErrorsToFormFields,
  scrollToFirstError,
} from '@/components/AdForm/AdForm.helpers';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { EditPopupType } from '@/components/Ad/Settings/Settings.types';
import { useAdDetails } from '@/hooks/useAdDetails';
import { EditAdPageTestIds } from '@/__e2e__/testIds/edit-ad-page';

export const EditAdForm: FC<EditAdFormProps> = ({ onSubmitError }) => {
  const { t } = useTranslation();
  const methods = useFormContext<EditAdFormFields>();
  const { initialAdDetails } = useServerDataContext();
  const { isLarge } = useScreenSize();
  const customPush = useCustomPush();
  const { refetch: refetchAdDetails } = useAdDetails({ skipQuery: true });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [updateAd] = useMutation<UpdateAdResponse>(UPDATE_AD);
  const {
    title,
    legacyResourceId,
    category,
    availableTemplatePhotos,
    properties,
  } = initialAdDetails ?? {};
  const { handleSubmit, control, setError, watch, formState } = methods;

  const photoIds = watch('photoIds');
  const isImagesLoading = photoIds?.some(({ id }) => id.endsWith('-loading'));
  const source = usePlatformSource();

  function onSubmit(data: EditAdFormFields) {
    if (!initialAdDetails?.id) return;

    setIsSubmitting(true);
    const adParams = buildUpdateAdAttributes(data);
    updateAd({
      variables: { adId: initialAdDetails.id, source, adParams },
    }).then(response => {
      if (!response.data?.updateAd) return;

      const { entity, errors = [] } = response.data.updateAd;

      const formErrors = mapApiErrorsToFormFields<EditAdError>(errors);

      if (formErrors.length) {
        onSubmitError(formErrors);

        formErrors.forEach(error => {
          setError(error.name, { message: error.message });
        });

        scrollToFirstError(formErrors);
        setIsSubmitting(false);
        return;
      }

      if (entity && entity.path) {
        refetchAdDetails().then(() => {
          customPush(`${entity.path}#${EditPopupType.OnReview}`);
        });
      } else {
        setIsSubmitting(false);
      }
    });
  }

  const renderSubmitBox = () => (
    <S.AdFormSubmitBox>
      <S.AdFormMsg $variant="2">
        <Trans
          i18nKey="pages.new_ad.agree_gdpr_policy"
          components={{
            link1: <CustomLink target="_blank" href={ROUTES.terms} />,
          }}
        />
      </S.AdFormMsg>
      <Button
        appearance="primary"
        isFullWidth
        disabled={isSubmitting || isImagesLoading || !formState.isDirty}
        type="submit"
        borderRadius={14}
      >
        {t('pages.edit_ad.apply_changes')}
      </Button>
    </S.AdFormSubmitBox>
  );

  return (
    <S.AdForm
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      data-testid={EditAdPageTestIds.form.form}
    >
      <S.AdFormSectionCard>
        <S.AdFormAdStaticInfoContainer>
          <S.AdFormAdStaticInfoTitle data-testid={EditAdPageTestIds.form.title}>
            {title}
          </S.AdFormAdStaticInfoTitle>
          <Typography.Caption
            $variant="2"
            data-testid={EditAdPageTestIds.form.adNumber}
          >
            {t('pages.ad.ad_number')}: {legacyResourceId}
          </Typography.Caption>
        </S.AdFormAdStaticInfoContainer>
        {properties &&
          properties.map(p => {
            switch (p.type) {
              case PropertyType.BooleanProperty: {
                return (
                  <Controller
                    key={p.legacyResourceId}
                    name={`boolean.${p.legacyResourceId}`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <Checkbox
                        {...field}
                        dataTestId={`checkbox-boolean-${p.legacyResourceId}`}
                        key={p.legacyResourceId}
                        appearance="filledCheckIcon"
                        label={p.name}
                        helperText={fieldState.error?.message}
                        hasError={!!fieldState.error}
                      />
                    )}
                  />
                );
              }
              default:
                return null;
            }
          })}
      </S.AdFormSectionCard>
      <S.AdFormSectionCard>
        <S.AdFormSectionTitle>{t('pages.edit_ad.price')}</S.AdFormSectionTitle>
        <Controller
          name="price"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              id="lot_price"
              appearance="filled"
              precision={2}
              inputMode="decimal"
              label={t('common.price_azn')}
              required={category?.isPriceRequired}
              helperText={fieldState.error?.message}
              hasError={!!fieldState.error}
            />
          )}
        />
      </S.AdFormSectionCard>
      <S.AdFormSectionCard>
        <S.AdFormSectionTitle>{t('common.description')}</S.AdFormSectionTitle>
        <Controller
          name="body"
          control={control}
          render={({ field, fieldState }) => (
            <Tooltip
              content={t('pages.new_ad.description_hint')}
              disabled={!isLarge}
            >
              <Textarea
                {...field}
                id="lot_body"
                appearance="quartenary"
                height={isLarge ? 245 : 140}
                placeholder={t('pages.new_ad.mark_benefits')}
                withSymbolsCount
                symbolsCountTextPlacement="bottom"
                helperText={fieldState.error?.message}
                helperTextAlignment="right"
                hasError={!!fieldState.error}
              />
            </Tooltip>
          )}
        />
      </S.AdFormSectionCard>

      <S.AdFormSectionCard>
        <S.AdFormSectionTitle>
          {t('common.photo')}{' '}
          {category?.isPhotoRequiredOnUpdate && <RequiredStar />}
        </S.AdFormSectionTitle>
        <Controller
          name="photoIds"
          control={control}
          render={({ field: { value, ...rest }, fieldState }) => (
            <AdImages
              {...rest}
              selected={value}
              presetImages={availableTemplatePhotos}
              helperText={fieldState.error?.message}
              hasError={!!fieldState.error}
            />
          )}
        />
      </S.AdFormSectionCard>

      <S.AdFormSectionCard>
        <S.AdFormSectionTitle>
          {t('pages.edit_ad.contact_details')}
        </S.AdFormSectionTitle>
        <ContactTypeFormSelect control={control} name="contactType" />

        {!isLarge && renderSubmitBox()}
      </S.AdFormSectionCard>

      {isLarge && renderSubmitBox()}
    </S.AdForm>
  );
};
